import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { API } from '@/api'
import { TeamSnapshotWithUser } from '@/api/type-response'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import { Button, message, Modal, Trans } from '@/components'
import { SNAP_STATUS_MAP } from '@/consts/snapshot'
import { TeamRole } from '@/consts/team'
import { Snapshot, SnapStatus } from '@/models/snapshot'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatBytes, formatDatePreset } from '@/utils/formatter'

import { showShareToTeamModal } from '../components/ShareToTeamModal'
import { SnapshotDetailContent } from '../components/SnapshotDetailContent'

type RouteSnapshotState = TeamSnapshotWithUser | Snapshot | undefined

function isTeamRecord(state: NonNullable<RouteSnapshotState>): state is TeamSnapshotWithUser {
  return 'snapshot' in state
}

export function MemberSnapshotDetailPage() {
  const { t } = useTranslation()
  const { snapshotId } = useParams<{ snapshotId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const userRole = useTeamUserRole()
  const teamStore = useTeamStore()
  const userStore = useUserStore()

  const snapshotData = location.state?.snapshot as RouteSnapshotState

  // Redirect if no data
  useEffect(() => {
    if (!snapshotData) {
      navigate(-1)
    }
  }, [snapshotData, navigate])

  // Don't render if no data
  if (!snapshotData) {
    return null
  }

  const fromTeamList = isTeamRecord(snapshotData)

  const id = fromTeamList ? snapshotData.snapshot.resID : snapshotData.resID
  const name = fromTeamList ? snapshotData.snapshot.flag?.subject || '-' : snapshotData.name || '-'
  const sizeBytes = fromTeamList ? Number(snapshotData.snapshot.sizeByte) : snapshotData.size
  const size = formatBytes(sizeBytes)
  const createTime = fromTeamList
    ? formatDatePreset(snapshotData.snapshot.requestTime, 'minute')
    : formatDatePreset(snapshotData.createdAt, 'minute')
  const status = fromTeamList ? snapshotData.snapshot.status : snapshotData.status
  const ownerName = fromTeamList
    ? snapshotData.user.user.nickname || snapshotData.user.user.name
    : userStore.name || '-'

  // Use centralized status mapping
  const statusInfo = SNAP_STATUS_MAP[status as keyof typeof SNAP_STATUS_MAP]
  const statusLabel = t(statusInfo.labelKey)
  const statusColor = statusInfo.style

  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(t('common.copied', 'Copied to clipboard'))
  }

  const isOwnSnapshot = !fromTeamList
  const canManage = userRole === TeamRole.Admin || userRole === TeamRole.Owner
  const canAct = canManage || isOwnSnapshot

  // Handle share to team
  const handleShareToTeam = () => {
    if (!teamStore.teamTokenData?.teamResID) return

    showShareToTeamModal({
      snapshotId: id,
      snapshotName: typeof name === 'string' ? name : '',
      ownerName,
      sizeInBytes: sizeBytes,
      teamResID: teamStore.teamTokenData.teamResID,
      onSuccess: () => {
        navigate(-1)
      },
    })
  }

  // Handle delete
  const handleDelete = () => {
    Modal.confirm({
      title: t('teamSnapshot.memberDetail.deleteModal.title', 'Delete Snapshot'),
      content: (
        <div className="bg-panel-gray-100 rounded-lg p-4 flex-row">
          <div className="t-sm text-secondary">
            <Trans i18nKey="teamSnapshot.memberDetail.deleteModal.content">
              Are you sure you want to delete this snapshot? This action cannot be undone and will
              permanently remove the snapshot.
            </Trans>
          </div>
        </div>
      ),
      confirmBtnCfg: {
        type: 'danger',
        text: t('common.delete', 'Delete'),
      },
      className: 'w-[640px]',
    }).then(async () => {
      try {
        if (fromTeamList) {
          await API.removeTeamMemberSnapshot({
            res_id: teamStore.teamTokenData!.teamResID,
            snap_res_id: id,
          })
        } else {
          await API.deleteSnapshot({ res_id: id })
        }
        message.success(
          t('teamSnapshot.memberDetail.deleteSuccess', 'Snapshot deleted successfully'),
        )
        navigate(-1)
      } catch (error) {
        console.error('Failed to delete snapshot:', error)
        message.error(t('teamSnapshot.memberDetail.deleteError', 'Failed to delete snapshot'))
      }
    })
  }

  return (
    <div className="card bg-panel-white p-8 flex flex-col gap-8 h-full overflow-y-auto">
      {/* Section 1: Breadcrumb Navigation */}
      <div className="border-b border-neutral-200 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to={fromTeamList ? '/team/snapshots/admin' : '/team/snapshots/member'}
              className="p-1 bg-panel-gray-100 rounded-md"
            >
              <ArrowLeftIcon className="w-6 h-6 text-tertiary" />
            </Link>
            <div className="h6">
              <span className="text-tertiary">
                <Trans i18nKey="snapshot.breadcrumb.snapshots">Snapshots</Trans>
              </span>
              <span> / </span>
              <span className="text-primary">{snapshotId}</span>
            </div>
          </div>

          {/* Action Buttons */}
          {canAct && (
            <div className="flex gap-3">
              <Button type="warning" size="md" onClick={handleDelete} className="min-w-[88px]">
                <Trans i18nKey="common.delete">Delete</Trans>
              </Button>
              {/* TODO(backend): enable once the change-owner API exists */}
              <Button type="outline" size="md" disabled>
                <Trans i18nKey="teamSnapshot.detail.changeOwner">Change Owner</Trans>
              </Button>
              <Button
                type="secondary"
                size="md"
                disabled={status === SnapStatus.Resumable}
                onClick={handleShareToTeam}
              >
                <Trans i18nKey="teamSnapshot.memberDetail.shareToTeam">Share with Team</Trans>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Snapshot Detail Content */}
      <SnapshotDetailContent
        id={id}
        name={name}
        size={size}
        createTime={createTime}
        statusLabel={statusLabel}
        statusColor={statusColor}
        ownerName={ownerName}
        // TODO(backend): pass the real owner history via `ownerLog` once the API exists
        onCopyId={handleCopy}
      />
    </div>
  )
}
